import { useState } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SEV_STYLE = {
  Critical: "text-red-300 border-red-500/40 bg-red-500/10",
  High: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  Medium: "text-blue-300 border-blue-500/40 bg-blue-500/10",
};

const STATUS_STYLE = {
  Active: "text-red-300 border-red-500/40 bg-red-500/10",
  Investigating: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  Triaged: "text-blue-300 border-blue-500/40 bg-blue-500/10",
  Mitigated: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
};

function timeAgo(iso) {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h`;
}

export default function ActiveAlertsTable({ alerts, onMitigated }) {
  const { tenant } = useTenant();
  const [busy, setBusy] = useState(null);

  const mitigate = async (a) => {
    setBusy(a.id);
    try {
      await api.post(`/soc/mitigate`, { alert_id: a.id, tenant: tenant.id, playbook: a.playbook });
      toast.success("Alert mitigated", {
        description: `IP ${a.source_ip} blocked at the edge`,
      });
      onMitigated?.();
    } catch {
      toast.error("Mitigation failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="overflow-x-auto max-h-[460px] overflow-y-auto" data-testid="active-alerts-table">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-[#0F172A]">
          <tr className="text-[10px] uppercase font-mono text-slate-500 border-b border-slate-800">
            <th className="text-left px-5 py-3">Source IP</th>
            <th className="text-left px-5 py-3">Destination</th>
            <th className="text-left px-5 py-3">Attack Type</th>
            <th className="text-center px-5 py-3">Severity</th>
            <th className="text-center px-5 py-3">Status</th>
            <th className="text-right px-5 py-3 pr-6">Action</th>
          </tr>
        </thead>
        <tbody>
          {alerts.map((a) => {
            const isMitigated = a.status === "Mitigated";
            return (
              <tr
                key={a.id}
                className={`border-b border-slate-800/40 transition-colors ${isMitigated ? "bg-emerald-500/[0.03]" : "hover:bg-[#1E293B]/40"}`}
                data-testid={`alert-row-${a.id}`}
              >
                <td className="px-5 py-2.5 font-mono text-xs text-slate-200">{a.source_ip}</td>
                <td className="px-5 py-2.5">
                  <div className="text-xs text-slate-200 font-semibold">{a.destination_unit}</div>
                  <div className="text-[9px] font-mono text-slate-500">{a.destination}</div>
                </td>
                <td className="px-5 py-2.5 text-xs text-slate-300">{a.attack_type}</td>
                <td className="px-5 py-2.5 text-center">
                  <span className={`text-[10px] font-mono font-bold px-2 py-0.5 border whitespace-nowrap ${SEV_STYLE[a.severity] || ""}`}>
                    {a.severity.toUpperCase()}
                  </span>
                </td>
                <td className="px-5 py-2.5 text-center">
                  <span
                    className={`text-[10px] font-mono font-bold px-2 py-0.5 border whitespace-nowrap ${STATUS_STYLE[a.status] || ""}`}
                    data-testid={`alert-status-${a.id}`}
                  >
                    {isMitigated && "● "}{a.status.toUpperCase()}
                  </span>
                </td>
                <td className="px-5 py-2.5 text-right pr-6">
                  {isMitigated ? (
                    <span className="text-[10px] font-mono text-emerald-400">BLOCKED · {timeAgo(a.detected_at)}</span>
                  ) : (
                    <button
                      data-testid={`mitigate-btn-${a.id}`}
                      onClick={() => mitigate(a)}
                      disabled={busy === a.id}
                      className="bg-[#1E3A8A] hover:bg-[#1D4ED8] disabled:opacity-50 text-white px-3 py-1.5 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 transition-colors"
                    >
                      {busy === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3" />}
                      {busy === a.id ? "Mitigating..." : "Mitigate"}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          {!alerts.length && <tr><td colSpan={6} className="px-5 py-10 text-center text-xs text-slate-500 font-mono">No active alerts</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
